import { useState, useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntity } from "./useEntity";
import { useEntityOperations } from "./useEntityOperations";
import { useEntitySearch, SearchPage } from "./useEntitySearch";
import { UserService } from "../services/userService";
import {
  BiaFitUser,
  CreateUserInput,
  UpdateUserInput,
  UserFilters,
} from "../types/user";

// Hook for managing users list with pagination and filtering
export const useUsers = (
  initialPage: number = 1,
  initialLimit: number = 10,
  initialFilters: UserFilters = {}
) => {
  const fetcher = useCallback<PaginatedFetcher<BiaFitUser, UserFilters>>(
    async (page, filters) => {
      const response = await UserService.getUsers(page, filters);
      return response ? pageFrom(response.users, response) : null;
    },
    []
  );

  const { items, ...rest } = usePaginatedResource<BiaFitUser, UserFilters>(
    fetcher,
    {
      initialPage,
      initialLimit,
      initialFilters,
      errorMessage: "Failed to fetch users",
    }
  );

  return { users: items, ...rest };
};

// Hook for managing individual user operations
export const useUserOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const createUser = useCallback(
    (userData: CreateUserInput) =>
      run(() => UserService.createUser(userData), "Failed to create user"),
    [run]
  );

  const updateUser = useCallback(
    (userId: number, updates: UpdateUserInput) =>
      run(
        () => UserService.updateUser(userId, updates),
        "Failed to update user"
      ),
    [run]
  );

  const deleteUser = useCallback(
    (userId: number) =>
      runBoolean(() => UserService.deleteUser(userId), "Failed to delete user"),
    [runBoolean]
  );

  return { createUser, updateUser, deleteUser, ...rest };
};

// Hook for getting individual user data
export const useUser = (userId: number | null) => {
  const fetcher = useCallback((id: number) => UserService.getUserById(id), []);

  const { data, setData, ...rest } = useEntity<BiaFitUser>(userId, fetcher, {
    notFoundMessage: "User not found",
    errorMessage: "An unexpected error occurred while fetching user",
  });

  return { user: data, setUser: setData, ...rest };
};

export const useUserSearch = () => {
  const fetcher = useCallback(
    async (
      searchTerm: string,
      page: number,
      limit: number
    ): Promise<SearchPage<BiaFitUser> | null> => {
      const response = await UserService.searchUsers(searchTerm, page, limit);
      if (!response?.data) return null;

      return {
        items: response.data.data,
        total: response.data.total,
        page: response.data.current_page,
        lastPage: response.data.last_page,
      };
    },
    []
  );

  const { search, ...rest } = useEntitySearch<BiaFitUser>(fetcher, {
    limit: 50,
    errorMessage: "Failed to search users",
  });

  return { search, searchUsers: search, ...rest };
};
